import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { forgotPassword, login, register, resetPassword } from '../services/api';
import { useT } from '../i18n/LangContext';

const ADMIN_EMAIL = 'admin@landing.com';
const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/+$/, '');

function getStrength(val) {
  let s = 0;
  if (val.length >= 8) s += 1;
  if (/[A-Z]/.test(val)) s += 1;
  if (/[0-9]/.test(val)) s += 1;
  if (/[^A-Za-z0-9]/.test(val)) s += 1;
  return s;
}

const strengthKeys = ['authPwMin', 'authPwWeak', 'authPwOkay', 'authPwStrong', 'authPwVeryStrong'];

export default function AuthPage() {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [pwStrength, setPwStrength] = useState(0);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [activeLetter, setActiveLetter] = useState(0);
  const { t, lang, setLang } = useT();
  const navigate = useNavigate();
  const location = useLocation();

  const isLogin = mode === 'login';
  const isReset = mode === 'reset';

  const activeHeadline = (isReset
    ? t('authResetPasswordTitle')
    : isLogin
      ? t('authSignBackIn')
      : t('authBuildPages')).replace(/\s+/g, ' ').trim();

  function switchMode(next) {
    setMode(next);
    setError('');
    setStatus('');

    if (next !== 'reset') {
      setResetToken('');
      setConfirmPassword('');
      if (mode === 'reset') {
        navigate('/auth', { replace: true });
      }
    }
  }

  function handlePwChange(val) {
    setPassword(val);
    setPwStrength(getStrength(val));
  }

  useEffect(() => {
    if (!activeHeadline.length) return undefined;

    setActiveLetter(0);
    const lettersCount = Array.from(activeHeadline).filter((ch) => ch !== ' ').length;
    if (lettersCount === 0) return undefined;

    const timer = setInterval(() => {
      setActiveLetter((prev) => (prev + 1) % lettersCount);
    }, 180);

    return () => clearInterval(timer);
  }, [activeHeadline]);

  useEffect(() => {
    const token = new URLSearchParams(location.search).get('resetToken');
    if (token) {
      setMode('reset');
      setResetToken(token);
      setPassword('');
      setConfirmPassword('');
      setError('');
      setStatus('');
      return;
    }

    if (mode === 'reset') {
      setMode('login');
      setResetToken('');
      setPassword('');
      setConfirmPassword('');
    }
  }, [location.search, mode]);

  function renderAnimatedHeadline(textLines) {
    let visibleIndex = -1;
    return textLines.map((line, lineIndex) => (
      <span key={lineIndex} className="auth-head-line">
        {Array.from(line).map((ch, charIndex) => {
          const isSpace = ch === ' ';
          if (!isSpace) visibleIndex += 1;
          const isActive = !isSpace && visibleIndex === activeLetter;

          return (
            <span
              key={`${lineIndex}-${charIndex}`}
              className={`auth-head-letter${isActive ? ' is-active' : ''}`}
            >
              {isSpace ? '\u00a0' : ch}
            </span>
          );
        })}
      </span>
    ));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError('');
    setStatus('');
    setLoading(true);

    try {
      if (isReset) {
        if (password !== confirmPassword) {
          throw new Error(t('authPasswordMismatch'));
        }

        const result = await resetPassword({ token: resetToken, password });
        localStorage.setItem('token', result.token);
        localStorage.setItem('userEmail', result.user.email);
        navigate('/', { replace: true });
        return;
      }

      // Admin login
      if (isLogin && email.trim().toLowerCase() === ADMIN_EMAIL) {
        const res = await fetch(`${API_BASE}/api/admin/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim(), password }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error?.message || 'Invalid admin credentials');
        localStorage.setItem('adminToken', data.token);
        navigate('/admin', { replace: true });
        return;
      }

      const action = isLogin ? login : register;
      const result = await action({ email, password });
      localStorage.setItem('token', result.token);
      localStorage.setItem('userEmail', result.user.email);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function onForgotPassword() {
    setError('');
    setStatus('');

    if (!email.trim()) {
      setError(t('authResetPasswordMissingEmail'));
      return;
    }

    setResetLoading(true);
    try {
      await forgotPassword({ email: email.trim() });
      setStatus(t('authResetLinkSent'));
    } catch (err) {
      setError(err.message || t('authResetPasswordError'));
    } finally {
      setResetLoading(false);
    }
  }

  const S = {
    page: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      minHeight: '100vh',
      fontFamily: 'Arial, sans-serif',
    },
    left: {
      background: isLogin
        ? 'linear-gradient(160deg, #1e1b4b 0%, #3730a3 45%, #1d4ed8 100%)'
        : isReset
          ? 'linear-gradient(160deg, #0f172a 0%, #1d4ed8 45%, #4f46e5 100%)'
          : 'linear-gradient(160deg, #064e3b 0%, #065f46 50%, #047857 100%)',
      display: 'flex',
      flexDirection: 'column',
      padding: '2.5rem 3rem',
      color: '#fff',
      position: 'relative',
      overflow: 'hidden',
    },
    leftGlow: {
      position: 'absolute',
      width: 500,
      height: 500,
      borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(255,255,255,0.07) 0%, transparent 70%)',
      top: -160,
      right: -160,
      pointerEvents: 'none',
    },
    leftGlow2: {
      position: 'absolute',
      width: 300,
      height: 300,
      borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
      bottom: -80,
      left: -80,
      pointerEvents: 'none',
    },
    topRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      position: 'absolute',
      top: '2rem',
      left: '3rem',
      zIndex: 2,
    },
    logo: {
      fontFamily: 'Arial, sans-serif',
      fontWeight: 900,
      fontSize: 16,
      letterSpacing: 3,
      textTransform: 'uppercase',
      color: 'rgba(255,255,255,0.95)',
    },
    langBox: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
    },
    langLabel: {
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: 1.5,
      textTransform: 'uppercase',
      color: '#ffffff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
    },
    langBtns: {
      display: 'flex',
      border: '1px solid #020617',
      borderRadius: 10,
      overflow: 'hidden',
      background: '#020617',
      boxShadow: '0 2px 10px rgba(15, 23, 42, 0.22)',
      justifyContent: 'center',
    },
    heroArea: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      position: 'relative',
      zIndex: 2,
      paddingTop: '4.5rem',
    },
    micro: {
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: 3,
      textTransform: 'uppercase',
      color: 'rgba(255,255,255,0.5)',
      marginBottom: '1rem',
    },
    heroH: {
      fontFamily: 'Arial, sans-serif',
      fontWeight: 900,
      fontSize: 'clamp(52px, 7vw, 80px)',
      lineHeight: 0.92,
      letterSpacing: -3,
      color: '#fff',
      marginBottom: '1.75rem',
    },
    heroHSignup: {
      fontFamily: 'Arial, sans-serif',
      fontWeight: 900,
      fontSize: 'clamp(28px, 3.2vw, 42px)',
      lineHeight: 1,
      letterSpacing: -1.5,
      color: '#fff',
      marginBottom: '1.4rem',
      whiteSpace: 'nowrap',
    },
    tagline: {
      fontSize: 15,
      color: 'rgba(255,255,255,0.65)',
      lineHeight: 1.8,
      maxWidth: 320,
    },
    perks: { display: 'flex', flexDirection: 'column', gap: 10 },
    perk: { display: 'flex', alignItems: 'flex-start', gap: 10 },
    perkGlyph: { fontSize: 15, color: 'rgba(255,255,255,0.6)', marginTop: 1, flexShrink: 0 },
    perkText: { fontSize: 14, color: 'rgba(255,255,255,0.8)', lineHeight: 1.55 },
    bottomTag: {
      fontSize: 12,
      color: 'rgba(255,255,255,0.4)',
      lineHeight: 1.7,
      position: 'relative',
      zIndex: 2,
    },
    right: {
      background: '#fff',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: '3rem clamp(2.5rem, 6vw, 5rem)',
      gap: '1.4rem',
      position: 'relative',
    },
    rightLang: {
      position: 'absolute',
      top: '2rem',
      right: '1.25rem',
      zIndex: 2,
      background: '#0f172a',
      padding: '0.85rem 1rem',
      borderRadius: 14,
      border: '1px solid #020617',
      boxShadow: '0 10px 30px rgba(15, 23, 42, 0.18)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    eyebrow: {
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: 2.5,
      textTransform: 'uppercase',
      color: '#4F46E5',
    },
    formTitle: {
      fontFamily: 'Arial, sans-serif',
      fontWeight: 900,
      fontSize: 'clamp(28px, 3.5vw, 36px)',
      color: '#0f172a',
      letterSpacing: -0.8,
      lineHeight: 1.1,
      marginBottom: 4,
    },
    resetNote: {
      marginTop: '-0.5rem',
      fontSize: 14,
      lineHeight: 1.6,
      color: '#64748b',
    },
    fieldGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
    fieldLabel: {
      fontSize: 12,
      fontWeight: 800,
      letterSpacing: 1,
      textTransform: 'uppercase',
      color: '#64748b',
    },
    fieldWrap: { position: 'relative' },
    prefix: {
      position: 'absolute',
      left: 14,
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#94a3b8',
      fontSize: 16,
      pointerEvents: 'none',
      fontWeight: 600,
    },
    input: {
      width: '100%',
      background: '#f8fafc',
      border: '1.5px solid #e2e8f0',
      borderRadius: 10,
      padding: '15px 16px',
      fontFamily: 'Arial, sans-serif',
      fontSize: 16,
      color: '#0f172a',
      outline: 'none',
      transition: 'border-color 0.15s, box-shadow 0.15s',
    },
    inputWithPrefix: {
      width: '100%',
      background: '#f8fafc',
      border: '1.5px solid #e2e8f0',
      borderRadius: 10,
      padding: '15px 16px 15px 38px',
      fontFamily: 'Arial, sans-serif',
      fontSize: 16,
      color: '#0f172a',
      outline: 'none',
      transition: 'border-color 0.15s, box-shadow 0.15s',
    },
    forgotBtn: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontFamily: 'Arial, sans-serif',
      fontSize: 12,
      color: '#4F46E5',
      textAlign: 'right',
      padding: '4px 0 0',
      textDecoration: 'underline',
      alignSelf: 'flex-end',
    },
    strengthRow: { display: 'flex', gap: 4, marginTop: 6 },
    strengthBar: (filled) => ({
      flex: 1,
      height: 3,
      borderRadius: 2,
      background: filled ? '#4F46E5' : '#e2e8f0',
      transition: 'background 0.2s',
    }),
    strengthHint: { fontSize: 12, color: '#94a3b8', marginTop: 3 },
    checkRow: { display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 2 },
    checkLabel: { fontSize: 12, color: '#64748b', lineHeight: 1.6 },
    submitBtn: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      background: '#4F46E5',
      color: '#fff',
      border: 'none',
      borderRadius: 10,
      padding: '17px 22px',
      fontFamily: 'Arial, sans-serif',
      fontSize: 17,
      fontWeight: 800,
      cursor: 'pointer',
      marginTop: 4,
      transition: 'opacity 0.15s, transform 0.12s',
    },
    errorBanner: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '12px 16px',
      background: 'rgba(239,68,68,0.07)',
      border: '1px solid rgba(239,68,68,0.3)',
      borderRadius: 8,
      color: '#ef4444',
      fontSize: 14,
    },
    switchLink: {
      fontFamily: 'Arial, sans-serif',
      fontSize: 15,
      color: '#64748b',
      textAlign: 'center',
    },
    switchBtn: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#4F46E5',
      fontFamily: 'Arial, sans-serif',
      fontSize: 15,
      fontWeight: 800,
      textDecoration: 'underline',
      padding: 0,
    },
  };

  function langBtn(code, badge, label) {
    const active = lang === code;
    return (
      <button
        key={code}
        type="button"
        onClick={() => setLang(code)}
        style={{
          background: active ? 'rgba(255,255,255,0.18)' : 'transparent',
          border: 'none',
          padding: '7px 12px',
          cursor: 'pointer',
          color: active ? '#fff' : 'rgba(255,255,255,0.55)',
          fontFamily: 'Arial, sans-serif',
          fontSize: 12,
          fontWeight: 800,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          transition: 'background 0.15s, color 0.15s, transform 0.15s',
          borderRight: code === 'en' ? '1px solid rgba(255,255,255,0.2)' : 'none',
        }}
        onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
        onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
      >
        <span style={{
          width: 18,
          height: 18,
          borderRadius: 999,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: active ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.12)',
          color: '#fff',
          fontSize: 11,
          fontWeight: 900,
        }}>
          {badge}
        </span>
        <span>{label}</span>
      </button>
    );
  }

  return (
    <div className="auth-page-wrap" style={S.page}>
      <div style={S.left}>
        <div style={S.leftGlow} />
        <div style={S.leftGlow2} />

        <div style={S.topRow}>
          <div style={S.logo}>FORMLY HR</div>
        </div>

        <div style={S.heroArea}>
          <div style={S.micro}>{isReset ? t('authResetPasswordTitle') : isLogin ? t('authWelcomeBack') : t('authGetStarted')}</div>
          <h1 style={isLogin ? S.heroH : S.heroHSignup}>
            {renderAnimatedHeadline([activeHeadline])}
          </h1>
        </div>
      </div>

      <div style={S.right}>
        <div style={S.rightLang}>
          <div style={S.langBox}>
            <div style={S.langLabel}>
              <span>🌐</span>
              <span>Language</span>
            </div>
            <div style={S.langBtns}>
              {langBtn('en', 'E', 'EN')}
              {langBtn('de', 'G', 'DE')}
            </div>
          </div>
        </div>

        <div>
          <div style={S.eyebrow}>{isLogin ? t('authAuthentication') : isReset ? t('authAuthentication') : t('authCreateAccount')}</div>
          <div style={S.formTitle}>
            {isReset ? t('authResetPasswordTitle') : isLogin ? t('authLoginTitle') : t('authSignupTitle')}
          </div>
        </div>

        {isReset && <div style={S.resetNote}>{t('authResetPasswordNote')}</div>}

        {error && (
          <div style={S.errorBanner}>
            <span>×</span>
            <span>{error}</span>
          </div>
        )}

        {status && (
          <div
            style={{
              ...S.errorBanner,
              background: 'rgba(16,185,129,0.08)',
              borderColor: 'rgba(16,185,129,0.25)',
              color: '#047857',
            }}
          >
            <span>✓</span>
            <span>{status}</span>
          </div>
        )}

        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
          {!isReset && (
            <div style={S.fieldGroup}>
              <label style={S.fieldLabel}>{t('authEmailLabel')}</label>
              <div style={S.fieldWrap}>
                <span style={S.prefix}>@</span>
                <input
                  type="email"
                  placeholder={t('authEmailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={S.inputWithPrefix}
                  onFocus={(e) => { e.target.style.borderColor = '#4F46E5'; e.target.style.boxShadow = '0 0 0 3px rgba(79,70,229,0.12)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
            </div>
          )}

          <div style={S.fieldGroup}>
            <label style={S.fieldLabel}>{isReset ? t('authNewPasswordLabel') : t('authPasswordLabel')}</label>
            <input
              type="password"
              placeholder={isReset ? t('authNewPasswordPlaceholder') : '••••••••••••••••'}
              value={password}
              onChange={(e) => handlePwChange(e.target.value)}
              minLength={8}
              required
              style={S.input}
              onFocus={(e) => { e.target.style.borderColor = '#4F46E5'; e.target.style.boxShadow = '0 0 0 3px rgba(79,70,229,0.12)'; }}
              onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
            />
            {!isLogin && (
              <>
                <div style={S.strengthRow}>
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} style={S.strengthBar(i < pwStrength)} />
                  ))}
                </div>
                <div style={S.strengthHint}>{t(strengthKeys[pwStrength])}</div>
              </>
            )}
            {isLogin && (
              <button type="button" style={S.forgotBtn} onClick={onForgotPassword} disabled={resetLoading}>
                {resetLoading ? t('authPleaseWait') : t('authResetPassword')}
              </button>
            )}
          </div>

          {isReset && (
            <div style={S.fieldGroup}>
              <label style={S.fieldLabel}>{t('authConfirmPasswordLabel')}</label>
              <input
                type="password"
                placeholder={t('authConfirmPasswordPlaceholder')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                minLength={8}
                required
                style={S.input}
                onFocus={(e) => { e.target.style.borderColor = '#4F46E5'; e.target.style.boxShadow = '0 0 0 3px rgba(79,70,229,0.12)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
              />
            </div>
          )}

          {!isLogin && !isReset && (
            <div style={S.checkRow}>
              <input type="checkbox" id="terms" required style={{ accentColor: '#4F46E5', marginTop: 3, flexShrink: 0 }} />
              <label htmlFor="terms" style={S.checkLabel}>
                {t('authTermsAgree')}{' '}
                <a href="/terms" style={{ color: '#4F46E5', textDecoration: 'underline' }}>{t('authTermsOfService')}</a>
                {' & '}
                <a href="/privacy" style={{ color: '#4F46E5', textDecoration: 'underline' }}>{t('authPrivacyPolicy')}</a>
              </label>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{ ...S.submitBtn, opacity: loading ? 0.7 : 1 }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.opacity = '0.9';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.transform = 'none';
            }}
          >
            <span>
              {loading
                ? t('authPleaseWait')
                : isReset
                  ? t('authResetPasswordTitle')
                  : isLogin
                    ? t('authSignIn')
                    : t('authCreateAccountBtn')}
            </span>
            <span style={{ fontSize: 20 }}>→</span>
          </button>
        </form>

        <div style={S.switchLink}>
          {isReset ? (
            <button type="button" style={S.switchBtn} onClick={() => switchMode('login')}>
              {t('authBackToLogin')}
            </button>
          ) : (
            <>
              {isLogin ? t('authNoAccount') : t('authHaveAccount')}{' '}
              <button type="button" style={S.switchBtn} onClick={() => switchMode(isLogin ? 'register' : 'login')}>
                {isLogin ? t('authCreateOne') : t('authSignInLink')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
